import React, { useRef, useEffect, useState } from "react";
import "@google/model-viewer";
import "./Viewer.css";
import { useDispatch, useSelector } from "react-redux";
import promptActions from "./data/promptActions.json";
import { getPromptMode, getPromptShowcase, setPromptMode } from "../redux_store/appState";
import { APP_JOB, APP_MODEL } from "../AppConfig";
import { fetchTimeout } from "../../components/Common";
import { API_BASE } from "../../AppConfig";

export const MODE_WEBXR = "webxr";
export const MODE_SCENE_VIEWER = "scene-viewer";
export const MODE_QUICK_LOOK = "quick-look";

export const PROMPT_ROTATE = 0;
export const PROMPT_ZOOM = 1;
export const PROMPT_PAN = 2;

const PROMPT_DELAY_MS = 5000;
const PROMPT_REPEAT_MS = 6000;

/**
 * Displays the <model-viewer> element for AR usage.
 * @param {object} props
 * @param {string|number} props.height – sets the height of the model viewer container
 */
export function Viewer({ height }) {
    const viewerRef = useRef(null);
    const [loadError, setLoadError] = useState(false);

    const promptMode = useSelector(getPromptMode);
    const showcase = useSelector(getPromptShowcase);

    const dispatch = useDispatch();

    useEffect(() => {
        let stopPrompts = false;

        const handleModelLoad = async () => {
            const apiUrl = `${API_BASE()}ar`;
        
            const getClientInfo = () => {
                const userAgent = navigator.userAgent;
                const platform = navigator.platform;
        
                let os = "Unknown";
                if (/Win/i.test(platform)) os = "Windows";
                else if (/Mac/i.test(platform)) os = "macOS";
                else if (/Linux/i.test(platform)) os = "Linux";
                else if (/Android/i.test(userAgent)) os = "Android";
                else if (/iPhone|iPad|iPod/i.test(userAgent)) os = "iOS";
        
                let browser = "Unknown";
                if (/Edg/i.test(userAgent)) browser = "Microsoft Edge";
                else if (/OPR/i.test(userAgent)) browser = "Opera";
                else if (/Chrome/i.test(userAgent)) browser = "Chrome";
                else if (/Safari/i.test(userAgent)) browser = "Safari";
                else if (/Firefox/i.test(userAgent)) browser = "Firefox";
                else if (/MSIE|Trident/i.test(userAgent)) browser = "Internet Explorer";
        
                const isMobile = /Mobi|Android/i.test(userAgent);
                const device = isMobile ? "Mobile" : "Desktop";
        
                return { device, os, browser, userAgent, platform };
            };
        
            try {
                const response = await fetch("https://ipapi.co/json/");
                const { ip = "Unknown", city = "Unknown", country_name = "Unknown" } = await response.json();
        
                const clientInfo = getClientInfo();
        
                const requestBody = {
                    ip,
                    jobId: APP_JOB,
                    city,
                    country: country_name,
                    ...clientInfo
                };
        
                await fetchTimeout(apiUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(requestBody),
                });
            } catch (error) {
                console.error("Error sending QRModelViewed request:", error);
            }
        };
        

        const handleCameraChange = (event) => {
            if (event.detail.source === "user-interaction") {
                stopPrompts = true;
            }
        };

        const handleModelError = (event) => {
            console.error("Model failed to load", event);
            setLoadError(true);
        };

        const goToNextPromptMode = () => {
            if (!stopPrompts) {
                dispatch(setPromptMode((promptMode + 1) % (PROMPT_PAN + 1)));
            }
        };

        const triggerPrompt = () => {
            if (stopPrompts) return;

            const viewerEl = viewerRef.current;
            if (!viewerEl) return;

            viewerEl.interact(PROMPT_DELAY_MS, promptActions[promptMode].finger0, promptActions[promptMode].finger1);

            const delay = showcase ? goToNextPromptMode : triggerPrompt;
            setTimeout(delay, PROMPT_REPEAT_MS);
        };

        const viewerEl = viewerRef.current;
        if (!viewerEl) return;

        viewerEl.addEventListener("load", handleModelLoad);
        viewerEl.addEventListener("camera-change", handleCameraChange);
        viewerEl.addEventListener("error", handleModelError);

        const isModelLoaded = viewerEl.modelIsVisible;
        if (isModelLoaded) {
            triggerPrompt();
        } else {
            viewerEl.addEventListener("poster-dismissed", triggerPrompt, { once: true });
        }

        return () => {
            stopPrompts = true;
            viewerEl.removeEventListener("load", handleModelLoad);
            viewerEl.removeEventListener("camera-change", handleCameraChange);
            viewerEl.removeEventListener("error", handleModelError);

            if (!isModelLoaded) {
                viewerEl.removeEventListener("poster-dismissed", triggerPrompt);
            }
        };
    }, [dispatch, promptMode, showcase]);

    return (
        <div style={{ height }}>
            {loadError ? (
                <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  fontWeight: "bold",
                }}
              >
                    <p>Failed to load 3D model. Please contact your dealer.</p>
                </div>
            ) : (
                <model-viewer
                    ref={viewerRef}
                    id="model-view"
                    preload
                    style={{ height: "100%" }}
                    camera-controls
                    camera-orbit="45deg 55deg auto"
                    touch-action="pan-y"
                    interaction-prompt="none"
                    src={APP_MODEL}
                    alt={`AR Viewer - ${APP_JOB}`}
                ></model-viewer>
            )}
        </div>
    );
}
